import { Link } from 'react-router-dom';
import { useT } from '../hooks/useT';
import { useSettingsStore } from '../store/settingsStore';
import logo from '../assets/logo140.png';

const FEATURE_KEYS = [
  { icon: '🤖', titleKey: 'landing.feature.ai.title', descKey: 'landing.feature.ai.desc' },
  { icon: '📊', titleKey: 'landing.feature.sim.title', descKey: 'landing.feature.sim.desc' },
  { icon: '💡', titleKey: 'landing.feature.rec.title', descKey: 'landing.feature.rec.desc' },
  { icon: '📈', titleKey: 'landing.feature.track.title', descKey: 'landing.feature.track.desc' },
];

const GOAL_KEYS = [
  { emoji: '🚗', key: 'landing.goal.car' },
  { emoji: '💍', key: 'landing.goal.wedding' },
  { emoji: '✈️', key: 'landing.goal.travel' },
  { emoji: '🎮', key: 'landing.goal.electronics' },
  { emoji: '🛡️', key: 'landing.goal.emergency' },
  { emoji: '🏠', key: 'landing.goal.real_estate' },
  { emoji: '💳', key: 'landing.goal.debt' },
];

const HOW_STEPS = [
  { label: 'landing.how.step1.label', title: 'landing.how.step1.title', desc: 'landing.how.step1.desc', icon: '🎯' },
  { label: 'landing.how.step2.label', title: 'landing.how.step2.title', desc: 'landing.how.step2.desc', icon: '💰' },
  { label: 'landing.how.step3.label', title: 'landing.how.step3.title', desc: 'landing.how.step3.desc', icon: '🚀' },
];

const TESTIMONIALS = [
  { quote: 'landing.trust.t1.quote', name: 'landing.trust.t1.name', role: 'landing.trust.t1.role' },
  { quote: 'landing.trust.t2.quote', name: 'landing.trust.t2.name', role: 'landing.trust.t2.role' },
  { quote: 'landing.trust.t3.quote', name: 'landing.trust.t3.name', role: 'landing.trust.t3.role' },
];

export default function LandingPage() {
  const t = useT();
  const { language, toggleLanguage } = useSettingsStore();

  return (
    <div className="min-h-screen bg-surface text-slate-100">

      {/* ── Nav ──────────────────────────────────────────────────────── */}
      <nav className="border-b border-surface-border px-6 py-4 sticky top-0 z-10 bg-surface/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Plannizzi AI" className="h-10 w-auto" />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-base text-primary">Plannizzi AI</span>
              <span className="text-xs text-slate-500 hidden sm:block">{t('brand.slogan')}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm transition-colors px-2 py-1 rounded-lg hover:bg-surface-card border border-transparent hover:border-surface-border"
              title={language === 'en' ? 'Mudar para Português' : 'Switch to English'}
            >
              🌐 {language === 'en' ? 'PT' : 'EN'}
            </button>
            <Link to="/pricing" className="text-slate-400 hover:text-slate-200 text-sm transition-colors hidden sm:block">{t('landing.nav.pricing')}</Link>
            <Link to="/login" className="btn-ghost text-sm">{t('landing.nav.sign_in')}</Link>
            <Link to="/register" className="btn-primary text-sm">{t('landing.nav.get_started')}</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-16 text-center">
        <div className="inline-flex items-center gap-2 bg-brand-900/40 border border-brand-800 text-brand-300 text-sm px-4 py-1.5 rounded-full mb-8">
          <span className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-pulse" />
          {t('landing.badge')}
        </div>

        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight">
          {t('landing.hero.title1')}<br />
          <span className="text-brand-400">{t('landing.hero.title2')}</span>
        </h1>

        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('landing.hero.sub')}
        </p>

        <div className="flex items-center gap-4 justify-center flex-wrap">
          <Link to="/register" className="btn-primary text-base px-8 py-3">
            {t('landing.hero.cta')}
          </Link>
          <Link to="/pricing" className="btn-secondary text-base px-8 py-3">
            {t('landing.hero.pricing')}
          </Link>
        </div>

        {/* Trust signals */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
          <span>{t('landing.hero.trust1')}</span>
          <span className="hidden sm:block text-slate-700">·</span>
          <span>{t('landing.hero.trust2')}</span>
          <span className="hidden sm:block text-slate-700">·</span>
          <span>{t('landing.hero.trust3')}</span>
        </div>

        {/* Goal chips */}
        <div className="mt-14 flex flex-wrap gap-3 justify-center">
          {GOAL_KEYS.map((g) => (
            <span key={g.key} className="flex items-center gap-2 bg-surface-card border border-surface-border px-4 py-2 rounded-xl text-sm text-slate-400">
              {g.emoji} {t(g.key)}
            </span>
          ))}
        </div>
      </section>

      {/* ── Problem / Solution ───────────────────────────────────────── */}
      <section className="border-y border-surface-border bg-surface-card/40 py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-4">{t('landing.problem.label')}</p>
          <p className="text-xl text-slate-300 leading-relaxed mb-8">
            {t('landing.problem.body')}
          </p>
          <p className="text-3xl font-bold text-brand-400">{t('landing.problem.solution')}</p>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {[
            { num: 'landing.stats.s1.num', label: 'landing.stats.s1.label', icon: '⚡' },
            { num: 'landing.stats.s2.num', label: 'landing.stats.s2.label', icon: '🗓️' },
            { num: 'landing.stats.s3.num', label: 'landing.stats.s3.label', icon: '📐' },
          ].map((s) => (
            <div key={s.num} className="card border-brand-800/30 bg-brand-950/10 py-8">
              <div className="text-4xl mb-2">{s.icon}</div>
              <p className="text-3xl font-bold text-brand-400 mb-1">{t(s.num)}</p>
              <p className="text-slate-400 text-sm">{t(s.label)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section className="border-t border-surface-border bg-surface-card/20 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4">{t('landing.how.title')}</h2>
            <p className="text-slate-400 max-w-xl mx-auto">{t('landing.how.sub')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-brand-700/0 via-brand-600/60 to-brand-700/0" />
            {HOW_STEPS.map((step, i) => (
              <div key={step.label} className="card text-center relative">
                <div className="w-10 h-10 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center mx-auto mb-4">
                  {i + 1}
                </div>
                <div className="text-3xl mb-3">{step.icon}</div>
                <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-1">{t(step.label)}</p>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">{t(step.title)}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{t(step.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4">{t('landing.features.title')}</h2>
          <p className="text-slate-400 max-w-xl mx-auto">{t('landing.features.sub')}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {FEATURE_KEYS.map((f) => (
            <div key={f.titleKey} className="card hover:border-brand-700/50 transition-colors group">
              <div className="text-3xl mb-4 group-hover:scale-110 transition-transform inline-block">{f.icon}</div>
              <h3 className="text-lg font-semibold text-slate-100 mb-2">{t(f.titleKey)}</h3>
              <p className="text-slate-400 leading-relaxed">{t(f.descKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Debt Payoff ───────────────────────────────────────────────── */}
      <section className="border-t border-surface-border bg-surface-card/20 py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-xs font-semibold text-brand-400 tracking-widest uppercase mb-4">
              {t('landing.debt.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4 leading-tight">
              {t('landing.debt.title')}
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8">{t('landing.debt.sub')}</p>
            <ul className="space-y-3 mb-8">
              {['avalanche', 'snowball', 'projection', 'savings'].map((k) => (
                <li key={k} className="flex items-start gap-3 text-slate-300 text-sm">
                  <span className="text-brand-400 mt-0.5 flex-shrink-0">✓</span>
                  {t(`landing.debt.point.${k}`)}
                </li>
              ))}
            </ul>
            <a href="/register" className="btn-primary inline-block">{t('landing.debt.cta')}</a>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: t('landing.debt.stat.strategy'), value: t('landing.debt.stat.strategy_val'), color: 'text-brand-400' },
              { label: t('landing.debt.stat.months'), value: t('landing.debt.stat.months_val'), color: 'text-sky-400' },
              { label: t('landing.debt.stat.interest'), value: t('landing.debt.stat.interest_val'), color: 'text-green-400' },
              { label: t('landing.debt.stat.schedule'), value: t('landing.debt.stat.schedule_val'), color: 'text-violet-400' },
            ].map((s) => (
              <div key={s.label} className="card text-center py-6">
                <p className={`text-2xl font-bold ${s.color} mb-1`}>{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section className="border-t border-surface-border bg-surface-card/20 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4">{t('landing.trust.title')}</h2>
            <p className="text-slate-400">{t('landing.trust.sub')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tm) => (
              <div key={tm.name} className="card flex flex-col gap-4">
                <div className="flex gap-0.5 text-amber-400 text-sm">
                  {'★★★★★'}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed flex-1">
                  "{t(tm.quote)}"
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="w-9 h-9 rounded-full bg-brand-700/50 border border-brand-600/50 flex items-center justify-center text-sm font-bold text-brand-300">
                    {t(tm.name).charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-200">{t(tm.name)}</p>
                    <p className="text-xs text-slate-500">{t(tm.role)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section className="border-t border-surface-border py-28 px-6 text-center relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[300px] bg-brand-600/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">{t('landing.cta.title')}</h2>
          <p className="text-slate-400 text-lg mb-10">{t('landing.cta.sub')}</p>
          <Link to="/register" className="btn-primary text-base px-10 py-4 inline-block">
            {t('landing.cta.button')}
          </Link>
          <p className="text-slate-600 text-sm mt-5">{t('landing.cta.note')}</p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="border-t border-surface-border py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Plannizzi AI" className="h-7 w-auto" />
            <span className="font-medium">Plannizzi AI</span>
          </div>
          <p>{t('landing.footer').replace('{year}', new Date().getFullYear())}</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms</Link>
            <Link to="/pricing" className="hover:text-slate-400 transition-colors">{t('landing.nav.pricing')}</Link>
            <Link to="/login" className="hover:text-slate-400 transition-colors">{t('landing.nav.sign_in')}</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}



